<template>
  <div class="view login">
    <div class="container">
      <div class="header">
        <SVGLogo />
        <span>{{ $t('header.title') }}</span>
      </div>
      <div class="content">

        <template v-if="state === STATE.DEFAULT">
          <h1>{{ $t('login.default.headline') }}</h1>
          <form @submit="handleDefault">
            <div class="group">
              <TextInput
                :placeholder="$t('login.formlabels.email')"
                :value.sync="email"
                :vuelidate="$v.email"
                autofocus
                fill
              />
            </div>
            <div class="group">
              <TextInput
                type="password"
                :placeholder="$t('login.formlabels.password')"
                :value.sync="password"
                :vuelidate="$v.password"
                fill
              />
            </div>
            <div class="group submit">
              <Button primary type="submit">{{ $t('login.formlabels.signin') }}</Button>
            </div>
            <div class="group links">
              <router-link to="/reset">{{ $t('login.formlabels.forgotPassword') }}</router-link><br/>
              <a href="mailto:schulpsychologie@vsa.zh.ch">{{ $t('login.formlabels.support') }}</a>
            </div>
          </form>
        </template>

        <template v-else-if="state === STATE.RESET">
          <h1>{{ $t('login.reset.headline') }}</h1>
          <p>{{ $t('login.reset.copy') }}</p>
          <form @submit="handleInitReset">
            <div class="group">
              <TextInput
                :placeholder="$t('login.formlabels.email')"
                :value.sync="email"
                :vuelidate="$v.email"
                autofocus
                fill
              />
            </div>
            <div class="group submit">
              <Button primary type="submit">{{ $t('login.formlabels.sendcode') }}</Button>
            </div>
            <div class="group links">
              <router-link to="/">{{ $t('login.formlabels.tologin') }}</router-link>
            </div>
          </form>
        </template>

        <template v-else-if="state === STATE.VERIFY">
          <h1>{{ $t('login.verify.headline') }}</h1>
          <p>{{ $t('login.verify.copy', { phone: phone }) }}</p>
          <form @submit="handleVerify">
            <div class="group">
              <TextInput
                ref="phoneInput"
                :placeholder="$t('login.formlabels.smscode')"
                :value.sync="code"
                :vuelidate="$v.code"
                fill
              />
            </div>
            <div class="group submit">
              <Button primary type="submit">{{ $t('login.formlabels.continue') }}</Button>
            </div>
            <div class="group links">
              <a @click.prevent.stop="state = STATE.DEFAULT" href="">{{ $t('login.formlabels.tologin') }}</a>
            </div>
          </form>
        </template>

        <template v-else-if="state === STATE.CHANGE">
          <h1>{{ $t('login.change.headline') }}</h1>
          <p>{{ $t('login.change.copy') }}</p>
          <form @submit="handleFinishReset">
            <div class="group">
              <TextInput
                type="password"
                :placeholder="$t('login.formlabels.password')"
                :value.sync="password"
                :vuelidate="$v.password"
                fill
              />
            </div>
            <div class="group">
              <TextInput
                type="password"
                :placeholder="$t('login.formlabels.passwordrepeat')"
                :value.sync="passwordRepeat"
                :vuelidate="$v.passwordRepeat"
                fill
              />
            </div>
            <div class="group submit">
              <Button primary type="submit">{{ $t('login.formlabels.setpassword') }}</Button>
            </div>
          </form>
        </template>
      </div>
      <div class="version">
        <router-link to="/changelog/" title="Mehr erfahren">
          <span class="ver" :class="{ new: isNewVersion }">v{{ $sav.version }}</span>
          <Icon name="info" />
          <span v-if="isNewVersion" class="notification-dot"/>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
  import SVGLogo from '@/assets/svg/app-logo.svg?inline';
  import TextInput from '@/components/ui/inputs/TextInput';
  import Button from '@/components/ui/inputs/Button';
  import Icon from '@/components/ui/Icon';
  import { Store } from '@/store';
  import { email, required, numeric, minLength, maxLength, sameAs } from 'vuelidate/lib/validators';

  const ACTION = { LOGIN: 'login', CHANGE: 'change' };
  const STATE = {
    DEFAULT: 'default',
    RESET: 'reset',
    VERIFY: 'verify',
    CHANGE: 'change',
  };

  export default {
    name: 'Login',
    components: {
      Icon,
      SVGLogo,
      Button,
      TextInput,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      reset: {
        type: Boolean,
        default: false,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        state: STATE.DEFAULT,
        action: null,
        phone: null,

        code: null,
        email: null,
        password: null,
        passwordRepeat: null,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      STATE() {
        return STATE;
      },
      isNewVersion() {
        return Store.versionCheck !== this.$sav.version;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      state(state) {
        this.code = state === STATE.DEFAULT ? null : this.code;
        this.password = null;
        this.passwordRepeat = null;
        this.$v.$reset();
      },
      reset: {
        handler(isReset) {
          this.state = isReset ? STATE.RESET : STATE.DEFAULT;
        },
        immediate: true,
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    validations() {
      const state = this.state;
      switch (state) {
        case STATE.RESET: {
          return {
            email: { email, required },
          };
        }
        case STATE.VERIFY: {
          return {
            code: {
              required,
              numeric,
              minLength: minLength(6),
              maxLength: maxLength(6),
            },
          };
        }
        case STATE.CHANGE: {
          return {
            password: { required, minLength: minLength(8) },
            passwordRepeat: { required, minLength: minLength(8), sameAsPassword: sameAs('password') },
          };
        }
        default: {
          return {
            email: { email, required },
            password: { required },
          };
        }
      }
    },
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      //----------------------------------
      // Event Handlers
      //----------------------------------
      async handleDefault(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.$v.$touch();
        if (!this.$v.$invalid) {
          this.action = ACTION.LOGIN;
          const response = await this.$sav.api.post('/auth/signin', null, {
            email: this.email.toLowerCase(),
            password: this.password,
          });
          if (response) {
            this.phone = response.phone;
            this.state = STATE.VERIFY;
            this.$nextTick(() => {
              this.$refs.phoneInput.focus();
            });
          }
        }
      },

      async handleVerify(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.$v.$touch();
        if (!this.$v.$invalid) {
          const response = await this.$sav.api.post('/auth/verify', null, {
            email: this.email.toLowerCase(),
            code: this.code,
          });

          if (response && response.info === 'authentication.success') {
            const action = response.action === 'reset' ? ACTION.CHANGE : this.action;
            if (action === ACTION.LOGIN) {
              this.state = STATE.DEFAULT;
              this.$emit('signedin', response);
            } else if (action === ACTION.CHANGE) {
              this.state = STATE.CHANGE;
            }
          } else {
            this.state = STATE.DEFAULT;
            if (this.$route.path !== '/') {
              this.$router.push('/');
            }
          }
        }
      },

      async handleInitReset(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.$v.$touch();
        if (!this.$v.$invalid) {
          this.action = ACTION.CHANGE;
          const response = await this.$sav.api.post('/auth/reset', null, {
            email: this.email.toLowerCase(),
          });

          if (response) {
            this.phone = response.phone;
            this.state = STATE.VERIFY;
          }
        }
      },

      async handleFinishReset(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.$v.$touch();
        if (!this.$v.$invalid) {
          const response = await this.$sav.api.post('/auth/reset', null, {
            email: this.email.toLowerCase(),
            password: this.password,
            code: this.code,
          });

          if (response) {
            this.state = STATE.DEFAULT;
            this.$emit('signedin', response);
          }
        }
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.view.login {
  position: relative;
  width: 100%;
  height: 100vh;

  & > .container {
    position: absolute;
    z-index: 2;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0;
    background-color: $color-white-solid-10;
    border-radius: $default-border-radius;
    width: 500px;
    filter: drop-shadow(0px 2px 4px rgba(16, 0, 0, 0.1));
    animation: login-appear 0.6s ease-out 0.4s forwards;

    & > div.header {
      background-color: $color-blue-solid-100;
      width: 100%;
      height: 75px;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      padding: 0 32px;
      user-select: none;

      & > svg {
        color: $color-white-solid-10;
        width: 40px;
        height: auto;
        margin-right: 12px;
      }

      & > span {
        @include font-logo;
        color: $color-white-solid-10;
      }
    }

    & > div.content {
      padding: 24px 32px 48px;

      h1 { user-select: none; }

      p { color: $color-black-solid-100; }

      form {
        padding-top: 32px;
        width: 100%;

        & >.group {
          width: 100%;
          margin-bottom: 24px;
          &:last-of-type {
            margin-bottom: 0;
          }
          &.submit {
            padding-top: 8px;
          }
          &.links a {
            @include font-body-small;
            color: $color-black-alpha-60;
            outline: 0;
            &:hover, &:focus {
              color: $color-blue-solid-100;
            }
          }
        }
      }
    }

    & > div.version {
      position: absolute;
      text-align: right;
      z-index: 1;
      bottom: 2px;
      right: 8px;
      user-select: none;
      pointer-events: none;

      & > a {
        display: inline-block;
        text-decoration: none;
        cursor: pointer;
        pointer-events: all;

        & > span.ver {
          @include font-body-small;
          font-weight: 600;
          color: $color-black-alpha-60;

          &.new {
            color: $color-black-alpha-80;
          }
        }

        & > svg {
          margin: 0 0 0 4px;
          position: relative;
          top: 1px;
          width: 12px;
          height: 12px;
          color: $color-black-alpha-60;
          pointer-events: all;
        }

        &:hover {
          & > span.ver, & > svg {
            color: $color-blue-solid-100;
          }
        }

        & > span.notification-dot {
          position: relative;
          display: inline-block;
          border-radius: 8px;
          top: -7px;
          margin-left: 2px;
          width: 6px;
          height: 6px;
          background-color: $color-blue-solid-100;

          &:before {
            content: '';
            display: block;
            position: absolute;
            border-radius: 8px;
            border: 1px solid $color-blue-solid-100;
            top: calc(50% - 3px);
            left: calc(50% - 3px);
            transform: scale(1);
            opacity: 1;
            width: 6px;
            height: 6px;
            background-color: transparent;
            animation: notify 3s linear 1s infinite;
          }
        }
      }
    }
  }
}
</style>
